import { IAreaTrendItem } from './IGlimpseNewReport';
import { PeopleCounter } from '../../report-components/models/PeopleCounter';
import { PosCategoryReportSop } from './PosCategorySop';

export type ConsolidationKeys = 'c' | 'nc' | 'nm';

export type IConsolidation = { [key in ConsolidationKeys]: IConsolidationResult };

export interface IConsolidationResult {
  pct: number;
  qty: number;
  top: string;
  val: number;
}

export enum InsightKind {
  Alert = 0,
  Performance = 1,
  BestPractice = 2,
  Insight = 3,
  Trend = 4,
}

export enum AttachmentKind {
  Unknown = -1,
  Image = 0,
  Chart = 1,
  Video = 2,
  Document = 3,
}

export interface IInsightItemAttachment {
  kind: AttachmentKind;
  isChart?: boolean;
  isImage?: boolean;
  isDocument?: boolean;
  isVideo?: boolean;
  itemAttachmentId: number;
  timestamp?: number;
  cameraId?: number;
  itemId: number;
  name: string;
  url: string;
  imageX?: number;
  imageY?: number;
}

export interface IInsightItem {
  kind: InsightKind;
  name: string;
  qty: number;
  text?: string;
  itemId: number;
  attachments?: IInsightItemAttachment[];
  executiveSummaryOnly: boolean;
  isGenerated: boolean;
  shortText?: string;
}

export interface IReportSettings {
  end: string;
  sectionId?: any;
  sopPost: number;
  sopPre: number;
  start: string;
  time: number;
  updated: string;
  matchStart?: string;
  matchEnd?: string;
}

export interface ICategory {
  cat: string;
  sales: number;
  transactions: number;
}

export interface IActivity {
  cats: ICategory[];
  consolidation: IConsolidation;
  id: number;
  name: string;
  sales: number;
  transactions: number;
  seen?: boolean;
}

export enum TransactionStat {
  lost = 0,
  atRisk = 1,
  compliant = 2,
}

export interface ITransaction {
  cameraId: number;
  cat: string;
  date: string;
  distance: number;
  id: number;
  images: string[];
  item: string;
  multi: boolean;
  qty: number;
  order: number;
  sId: string;
  sectionId: number;
  server: string;
  /**
   * What the cashier typed alongside the product. On a valet ticket this is where the vehicle and
   * its ticket number are, the product name carrying neither. Absent on every other vertical.
   */
  comment?: string;
  stat: TransactionStat;
  val: number;
  mId: string | null;
  mdId: string | null;
  pt: number;
  bboxes?: string[];
  trackId: number;
  x: number;
  y: number;
  spaRoom?: string;
  spaSecondsInRoom?: number;
}

export interface ICamera {
  events: number;
  id: number;
  images: number;
  name: string;
  templateId: number;
  transactions: number;
  mask: string;
  mlMask: string;
  isQsrQueue: boolean;
}

export interface IAuditTime {
  start: string;
  end: string;
  auditedMinutes: number;
}

export interface ICategoryConsolidation {
  cPct: number;
  cPct2: number | null;
  cPct3: number | null;
  cQ: number;
  cVal: number;
  cat: string;
  ncPct: number;
  ncPct2: number | null;
  ncPct3: number | null;
  ncQ: number;
  ncVal: number;
  nmPct: number;
  nmPct2: number | null;
  nmPct3: number | null;
  nmQ: number;
  nmVal: number;
  periodId: string | null;
  sales: number;
  transactions: number;
}

export interface ISection {
  id: number;
  name: string;
}

export enum AuditStatus {
  Cancelled = -2,
  Disabled = -1,
  NotPublished = 0,
  Published = 1,
}

export interface Sale {
  stat: TransactionStat;
  checkNo: string;
  order: number;
  tId: number;
  sectionId: number;
  id: string;
  date: string;
  server: string;
  cat: string;
  item: string;
  val: number;
  mId: string | null;
  mdId: string | null;
}

export interface DuplicateAudit {
  auditId: number;
  camera: string;
  cameraId: number;
  category: string;
  date: string;
  elapsedTime: number;
  imageUrl: string;
  location: string;
  locationId: number;
  otherCamera: string;
  otherCameraId: number;
  otherImageUrl: string;
  quantity: number;
}

export interface DataQuality {
  auditDuplicates: DuplicateAudit[];
  missingBarEquivalencies: {
    branchId: number;
    terminal: string;
  }[];
  missingCategoryPrice: any[];
  missingCategorySop: number;
  missingPOSCategorization: number;
  parsingResult: {
    failed: number;
    review: number;
  };
  reviewPOSCategorization: number;
  missingCategoryEquivalencies: number;
}

export interface ProductionStat {
  category: string;
  avgProductionTime: number;
  subwayAverage: number;
  fastest: number;
}

export interface UpsellStats {
  stats: {
    food: number;
    drink: number;
    snack: number;
    total: number;
    waitedLongerThanAverage: number;
  } | null;
  benchmarking: {
    averagePerCustomer: number;
    bestPerCustomer: number;
  } | null;
  opportunity: {
    food: number;
    foodCategory: string;
    snack: number;
    snackCategory: string;
    drink: number;
    drinkCategory: string;
    throughputInefficiency: number;
  } | null;
  customerCount: number;
}

export interface EmployeeClocking {
  date: string;
  clockIn: string;
  clockOut: string;
  employeeName: string;
  employeeTag: string;
  hourlyWage: number;
}

export interface IReportPresentationSettings {
  features: Features[];
  /**
   * True when the report was built by the async matching pipeline rather than legacy consolidation.
   * Absent on every report published before the flag existed, which are all legacy.
   */
  asyncMatching?: boolean;
  /** How async matching paired the day's tags with its sales. Absent reads as regular. */
  matchingMethod?: MatchingMethod;
}

/** How async matching pairs a day's tags with its sales, chosen per report. See Matching.Core/matchers.md D35. */
export enum MatchingMethod {
  Regular = 'Regular',
  Strict = 'Strict',
}

export interface AuditFilters {
  employees: string[];
  categories: string[];
}

export enum Features {
  VideoAuditing = 'VideoAudit',
  CustomerCounting = 'CustomerCounting',
  CustomerCountingMatching = 'CustomerCountingMatching',
  CustomerInOutMatching = 'CustomerInOutMatching',
  AnprMatching = 'AnprMatching',
}

export interface IGlimpseReport {
  activity?: IActivity[];
  allTransactions?: ITransaction[];
  allSales?: Sale[];
  area: string;
  areaId: number;
  auditTimes: IAuditTime[];
  auditType: number;
  cameras?: ICamera[];
  cats?: ICategoryConsolidation[];
  consolidation: IConsolidation;
  customer: string;
  customerId: number;
  date: string;
  day: number;
  dayOfTheWeek: string;
  end: string;
  endTime: Date;
  isArchived: boolean;
  isWeekend: boolean;
  items: IInsightItem[];
  itemsDetail?: IInsightItem[];
  location: string;
  locationId: number;
  module: string;
  month: number;
  monthName: string;
  monthPeriodId: string;
  period: number;
  periodId: string;
  sales: number;
  score: number;
  sections?: ISection[];
  filters: AuditFilters;
  settings: IReportSettings;
  presentationSettings: IReportPresentationSettings;
  sopSettings: PosCategoryReportSop;
  start: string;
  startTime: Date;
  status: AuditStatus;
  time: number;
  transactions: number;
  trends?: IAreaTrendItem[];
  weekNumber: number;
  year: number;
  lockedBy: string;
  lastPublishedBy: string;

  // Audit Preview
  parsedFiles: number;
  totalFiles: number;
  quality?: DataQuality;

  // Subway fields
  startTags?: ITransaction[];
  productionStats?: ProductionStat[];
  upsellStats: UpsellStats;
  employeeClockings?: EmployeeClocking[];
  peopleCountStats?: PeopleCounter;
}
